import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PiggyBank, Target, PartyPopper } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CONFETTI = ["#2EC4B6", "#FF6B6B", "#facc15", "#34d399"];

export default function SavingsTracker({ budget }) {
  const weeks = budget?.weeks || [];
  const perWeek = weeks.map((w) => Number(w.expenses?.savings?.amount) || 0);
  const total = perWeek.reduce((a, b) => a + b, 0);
  const max = Math.max(total, 1);

  const [goal, setGoal] = useState(0);
  const [goalInput, setGoalInput] = useState("");
  const [burst, setBurst] = useState(false);
  const prevReached = useRef(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const g = r.data.savings_goal || 0;
      setGoal(g); setGoalInput(g ? String(g) : "");
    }).catch(() => {});
  }, []);

  const saveGoal = async () => {
    const val = Math.max(0, parseFloat(goalInput) || 0);
    setGoal(val);
    api.put("/profile", { savings_goal: val }).catch(() => {});
  };

  const reached = goal > 0 && total >= goal;
  useEffect(() => {
    if (reached && !prevReached.current) {
      setBurst(true);
      prevReached.current = true;
      const t = setTimeout(() => setBurst(false), 1600);
      return () => clearTimeout(t);
    }
    if (!reached) prevReached.current = false;
  }, [reached]);

  const pct = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 0;
  let cum = 0;
  const rows = perWeek.map((v, i) => { cum += v; return { wk: i + 1, cum }; });

  return (
    <section data-testid="snap-savings" className="relative overflow-hidden bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      {burst && (
        <div className="pointer-events-none absolute inset-0" data-testid="snap-celebrate">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} className="snap-confetti" style={{ left: `${(i * 5.5) % 100}%`, background: CONFETTI[i % 4], animationDelay: `${(i % 9) * 0.05}s` }} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <PiggyBank className="w-5 h-5" /> Savings Jar
        </h2>
        {reached ? (
          <span data-testid="snap-goal-badge" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
            <PartyPopper className="w-3 h-3" /> Goal reached!
          </span>
        ) : (
          <span className="text-xs text-slate-500">this month</span>
        )}
      </div>

      <div className="flex items-end gap-2 mb-3">
        <PiggyBank className="w-9 h-9 text-emerald-400" />
        <span data-testid="snap-savings-total" className={`font-mono text-3xl font-bold text-emerald-400 ${total > 0 ? "cc-gold-positive" : ""}`}>{money(total)}</span>
      </div>

      <div className="flex items-center gap-2 mb-2 text-xs">
        <Target className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="text-slate-400 whitespace-nowrap">Monthly goal</span>
        <input
          type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} onBlur={saveGoal}
          placeholder="0.00" data-testid="snap-savings-goal"
          className="ml-auto w-24 h-8 px-2 rounded bg-slate-900/70 border border-white/10 text-right font-mono text-emerald-200 focus:outline-none focus:border-emerald-500"
        />
      </div>
      <div className="h-3 bg-white/[0.05] rounded-full overflow-hidden mb-1">
        <div data-testid="snap-savings-progress" className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? "bg-emerald-400" : "bg-emerald-500/70"}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        {goal <= 0 ? "Set a monthly goal to track your progress"
          : reached ? `Goal smashed! ${money(total)} of ${money(goal)}`
          : `${pct}% of ${money(goal)} — ${money(Math.max(0, goal - total))} to go`}
      </p>

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.wk} className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-[11px] text-slate-500">Wk {r.wk}</span>
            <div className="flex-1 h-4 bg-white/[0.04] rounded overflow-hidden">
              <div className="h-full bg-emerald-500/50 rounded transition-all duration-500" style={{ width: `${Math.round((r.cum / max) * 100)}%` }} title={`running total ${money(r.cum)}`} />
            </div>
            <span className="w-20 shrink-0 text-[11px] font-mono text-right text-slate-300">{money(r.cum)}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-4">
        Add to the <span className="text-emerald-300">Savings</span> row in each week and watch your jar fill. This also feeds your Wish List spare-cash goal.
      </p>
    </section>
  );
}
